import React, { useState } from "react"

// internal modules
import PieSelect from "app/static/frontend/selects/components/PieSelect"
import searchActionTypes from "search-new/action-types"
import { getNestedObjectKey, idFromResourceUri } from "shared/imports/sharedHelperFunctions"
import { setClearableFilter } from "shared/selects/helperFunctions"
import {
    AgencySearchifyFLV,
    AlertFilterableListView,
    AlertFeedItemFilterableListView,
    AlertGroupFilterableListView,
    AmendmentFilterableListView,
    ArticleFilterableListView,
    ArticleTopicSearchifyFLV,
    AssignmentFilterableListView,
    BillFilterableListView,
    BulkEmailFilterableListView,
    BulkSMSFilterableListView,
    CampaignFilterableListView,
    CanadianFederalElectoralDistrictSearchifyFLV,
    CaucusFilterableListView,
    CommitteeFilterableListView,
    CountrySearchifyFLV,
    CountySearchifyFLV,
    CustomDataSearchifyFLV,
    DistrictSearchifyFLV,
    DocumentFilterableListView,
    DocketFilterableListView,
    ElectionFilterableListView,
    EventFilterableListView,
    ExecutiveSearchifyFLV,
    GrassrootsActionFilterableListView,
    GeoShapeSearchifyFLV,
    IssueManagementFilterableListView,
    IssueSearchifyFLV,
    LegislativeSessionSearchifyFLV,
    LegislatorSearchifyFLV,
    LowerLDSearchifyFLV,
    MunicipalitySearchifyFLV,
    NewListFilterableListView,
    NoteFilterableListView,
    OfficialFilterableListView,
    OfficialsAndStaffEUFilterableListView,
    OrganizationSearchifyFLV,
    PacClassicPacOfficeFilterableListView,
    PacClassicPacOrganizationFilterableListView,
    PacClassicPacTransactionFilterableListView,
    PublicOrganizationFilterableListView,
    PressContactFilterableListView,
    RegulationFilterableListView,
    RegulationDocumentFilterableListView,
    SendEmailFilterableListView,
    StafferFilterableListView,
    StafferTermSearchifyFLV,
    StafferTitleSearchifyFLV,
    StateSearchifyFLV,
    SupporterFilterableListView,
    TeamSearchifyFLV,
    TrackingDashboardFilterableListView,
    UpperLDSearchifyFLV,
    UserFilterableListView,
    VoteFilterableListView,
    VoteResultFilterableListView,
    PoliticalCommitteeFilterableListView,
    TransactionFilterableListView,
    BeatSearchifyFLV,
    SocialDocumentFilterableListView,
    PoliticalCommitteeCampaignFinanceFilterableListView,
    CampaignFinanceDisbursementsFilterableListViewFLVs,
    CampaignFinanceReceiptsCandidatesFilterableListViewFLVs,
    SupporterLegislatorSearchifyFLV,
    PoliticalCommitteeLegislatorSearchifyFLV,
    LegislatorSearchifyWithoutCampaignFinanceFLV,
    OfficialFilterableWithoutCampaignFinanceListView,
} from "shared/search/containers/FilterableListView/FilterableListViews"

import { getCharities } from "@/services/charities"

import genericEnumSelect from "./EnumSelect"

const NUM_SELECTIONS_TO_SWAL_ON_CLEAR = 5
export const API_META_LIMIT = 20
const API_META_LIMIT_CANDID = 25

export const getItems = ({ item, label, returnAllResponse, extraFields }) => {
    const isDisbursementPoliticalFilter = label === "recipient_title"
    return {
        // some Selects require all fields returned by the response
        ...(returnAllResponse ? { ...item } : {}),
        // some Selects require a couple of extra fields returned by the response
        ...(extraFields
            ? extraFields.reduce((accumulator, extraField) => {
                  accumulator[extraField] = item[extraField]
                  return accumulator
              }, {})
            : {}),
        value: isDisbursementPoliticalFilter ? item.recipient_id : item.id,
        id: isDisbursementPoliticalFilter ? item.recipient_id : item.id,
        resource_uri: item.resource_uri,
        [label]: item[label],
    }
}

/**
 * Check if the filter component received a 'permanentFilter' prop with specified filters
 * If so, return the permanent filter passed in as props
 * If not, reutrn the default permanent filter defined on the filter component
 * @name getPermanentFilter
 * @function
 * @param {Object} propPermanentFilter - The permanent filter passed as props to the filter component
 * @param {Number} defaultPermanentFilter - A default permant filter defined on the filter component
 * @returns {Object} permanentFilter - The permanent filter for use in the filter component
 */
export const getPermanentFilter = (propPermanentFilter, defaultPermanentFilter) =>
    propPermanentFilter && Object.keys(propPermanentFilter).length ? propPermanentFilter : defaultPermanentFilter

/**
 * Takes a list of objects and removes objects with duplicate IDs.
 * In the case of duplicates, only the first object with duplicate ID is included.
 *
 * @param {*} optionsMap an array of objects that have id keys
 *
 * @returns Array of objects
 */
export const getOptionsMapDistinct = (optionsMap) => {
    let distinctOptionsMap = []
    const idSet = new Set()
    optionsMap.forEach((option) => {
        const id = option.id
        if (!idSet.has(id)) {
            distinctOptionsMap.push(option)
            idSet.add(option.id)
        }
    })
    return distinctOptionsMap
}

// this function simplifies generating different PieSelects.
// this works for single or multiple select
// we set up:
// - resource
// - label
// - placeholder
// - multi
// - permanentFilter
// calling code specifies:
// - onChange
// - onLoadOptions: optional callback when options are ready
// - value (can be used if you want a value already populated)
// - anything else desired ex. label
export const GenericPieSelect = ({ allowExpandUp, resource, onLoadOptions, permanentFilter = {}, ...props }) => {
    // load options will be the aggregation of all these resolved promises
    const loadOptions = (filter) => {
        // initialize empty array of promises
        const promises = []
        let hasMore = false
        let nextOffset = 0

        // non-Quorum external resource(s) which require(s) custom parsing
        if (props.customGetter) {
            // do not fire the request if the length of the query is under the custom char limit for the resource
            // i.e., we do not want to spam the Candid API and incur extra costs since they bill per-query
            if (props.quickSearchMinimumChars && filter.quick_search.length < props.quickSearchMinimumChars) {
                return Promise.all(promises).then((payloads) => {
                    return {
                        options: [],
                    }
                })
            }

            promises.push(
                props
                    .customGetter({
                        // filter.quick_search is hardcoded into the Select logic
                        [props.customFilterLabel]: filter.quick_search,
                        [props.customOffsetLabel]: filter.offset,
                    })
                    .then((response) => {
                        if (!response) {
                            return []
                        }

                        if (response.hits.length === props.apiMetaLimit) {
                            hasMore = true
                            nextOffset = filter.offset + props.apiMetaLimit
                        }

                        return response.hits.map((item) => ({
                            value: props.customValue(item),
                            id: props.customId(item),
                            [props.label]: props.customLabel(item),
                        }))
                    }),
            )
        } else {
            promises.push(
                resource.objects
                    .filter(filter)
                    .GET({ shouldLoadFromCache: true })
                    .then((resp) => {
                        if (!resp) {
                            return []
                        }

                        // if this request returns less than the tastypie limit,
                        // then we know that there is no more data to load
                        if (resp.objects.length === API_META_LIMIT) {
                            hasMore = true
                        }

                        const next = resp.meta.next
                        const regex = next && next.match(/&offset=(\d+)/)
                        if (regex.length) {
                            nextOffset = regex[1]
                        }

                        // resource.associatedQDT().value === DjangIO.app.models.QuorumDataType.thing.value

                        return resp.objects.map((item) =>
                            getItems({
                                item,
                                label: props.label,
                                returnAllResponse: props.returnAllResponse,
                                extraFields: props.extraFields,
                            }),
                        )
                    }),
            )
        }

        const initialValuesJS =
            filter.initialValues && filter.initialValues.toJS
                ? // some forms erroneously return immutable arrays to frontend components
                  // (i.e., they are not wrapped in withImmutablePropsToJS),
                  // which means that we need to coerce them back to javascript arrays
                  // in order for the .GET request below to not error out
                  filter.initialValues.toJS()
                : filter.initialValues

        // We need to load any initial values to populate the select.

        // This is necessary because props.initialValues is an array that can contain
        // hundreds of individually selected ids.

        // Since we only load the first 20 rows from resource.objects in the above .GET(),
        // this means that any rows which were not included in the first 20 returned in the .GET
        // will show up as blank.

        // To fix this, we fire a single query which will return the rest of the initialValues
        // and then insert them into the Select's options prop in order to visualize them within the select.
        const initialValuesIsArray = Array.isArray(initialValuesJS)
        if ((initialValuesIsArray && initialValuesJS.length) || (!initialValuesIsArray && initialValuesJS)) {
            // this handles the case where we have a single or a multi select
            const initialValues = props.multi ? initialValuesJS : [initialValuesJS]

            const request = resource.objects

            // We want to pass permanentFilters in the case where we pass
            // { default_for_user: false } from the frontend in order to
            // satisfy is_request_with_superuser_privileges(request) in the
            // QuorumUser for_user
            if (
                permanentFilter &&
                // TODO why can't we always pass the permanentFilter?
                (permanentFilter.default_for_user === false || permanentFilter.dehydrate_extra)
            ) {
                request.filter(permanentFilter)
            }

            if (props.customGetter) {
                promises.push(
                    props
                        .customGetter({
                            [props.customFilterLabel]: initialValues,
                        })
                        .then((response) => {
                            if (!response) {
                                return []
                            }

                            return response.hits.map((item) => ({
                                value: props.customValue(),
                                id: props.customId(),
                                [props.label]: props.customLabel(item),
                            }))
                        }),
                )
            } else {
                promises.push(
                    request
                        .filter({
                            id__in: initialValues,
                            limit: 0,
                        })
                        .GET({ shouldLoadFromCache: true })
                        .then((resp) => {
                            if (!resp) {
                                return {}
                            }

                            return resp.objects.map((item) =>
                                getItems({
                                    item,
                                    label: props.label,
                                    returnAllResponse: props.returnAllResponse,
                                    extraFields: props.extraFields,
                                }),
                            )
                        }),
                )
            }
        }

        return Promise.all(promises).then((payloads) => {
            const options = payloads.reduce((currentArr, nextPayload) => {
                // if no objects are returned and it is not an object, return the
                // current list of objects
                if (!nextPayload.length && !nextPayload.id) {
                    return currentArr
                    // if the next payload is simply an object, make sure
                    // it isn't already included
                } else if (!nextPayload.length && nextPayload.id) {
                    return currentArr.filter((item) => item.id !== nextPayload.id).concat(nextPayload)
                }
                return currentArr.concat(nextPayload)
            }, [])

            // respond to a prop passed by a parent that does not render an X button for a select
            // if the payload inclues a default boolean flag. Adding this to prevent default options in
            // selects from rendering with an X buttion
            const { disableRemoveOption, disableRemoveOptionKey } = props
            const thisContext = { disableRemoveOption, disableRemoveOptionKey }
            let optionsMap = options.map(setClearableFilter, thisContext)

            if (props.shouldBeUniqueId) {
                optionsMap = getOptionsMapDistinct(optionsMap)
            }

            if (onLoadOptions) {
                onLoadOptions(optionsMap)
            }

            return {
                options: optionsMap,
                hasMore,
                nextOffset,
            }
        })
    }
    return (
        <PieSelect
            {...props}
            allowExpandUp={allowExpandUp}
            loadOptions={loadOptions}
            permanentFilter={permanentFilter}
            onChange={(nextValue) => {
                // if user pressed the 'x' to clear all selections, value will be empty array
                if (
                    (!nextValue || nextValue.length === 0 || Object.keys(nextValue).length === 0) &&
                    !props.noSwalWhenClearingMany &&
                    props.value
                ) {
                    // if there are many current selections, swal the user to make sure they want to clear them all
                    if (props.value.length >= NUM_SELECTIONS_TO_SWAL_ON_CLEAR) {
                        swal({
                            closeOnClickOutside: true,
                            closeOnEsc: true,
                            icon: "warning",
                            title: "Are you sure?",
                            text: `Would you like to clear all ${props.value.length} of your selections?`,
                            buttons: {
                                cancel: "No",
                                confirm: "Yes",
                            },
                        }).then((swalButton) => {
                            if (swalButton) {
                                props.onChange([])
                            }
                        })
                    }
                    // if there aren't many selections, then trust that user is sure about this
                    else {
                        props.onChange([])
                    }
                }
                // if the change was not clearing all selections, then carry on as usual
                else {
                    props.onChange(nextValue)
                }
            }}
        />
    )
}
export default GenericPieSelect

// this function is used for setting up searchify for any of the PieSelect components
export const generateAdvancedSearchifyConfig = (
    props,
    searchifyFLV,
    permanentFilter = {},
    searchifyInitialFilters = undefined,
) => {
    // if you want a select without searchify capabilities
    if (!props.searchify) {
        return false
    }

    if (!searchifyFLV) {
        console.warn("generateAdvancedSearchifyConfig: did not get a searchifyFLV")
    }
    const combinedPermanentFilter = Object.assign(props.permanentFilter || {}, permanentFilter)

    return props.searchifyConfigs
        ? Object.assign({}, props.searchifyConfigs, {
              searchifyPermanentFilter: combinedPermanentFilter,
              searchifyFLV: searchifyFLV || props.searchifyConfigs.searchifyFLV,
              searchifyInitialFilters: searchifyInitialFilters || props.searchifyConfigs.searchifyInitialFilters,
          })
        : {
              searchifyFLV,
              searchifyInitialAddedIds:
                  props.singleSelectSearchify && props.value
                      ? [props.value]
                      : (props.value && props.value.length && props.value.map((val) => val)) ||
                        props.value ||
                        undefined,
              searchifyInitialFilters: props.initialFilter || searchifyInitialFilters || {},
              searchifyPermanentFilter: combinedPermanentFilter,
              singleSelectSearchify: props.singleSelectSearchify,
              singleFilterSearchify: props.singleFilterSearchify,
              searchifyHeaderName: props.searchifyHeaderName,
              submitSearchifyFunc: props.submitSearchifyFunc || searchActionTypes.SELECT_SUBMIT_SEARCHIFY,
              callBack: props.searchifyCallBack,
              maxSearchifyCount: props.maxSearchifyCount,
              disableSelectAllSearchify: props.disableSelectAllSearchify,
              shouldOpenSearchifyModal: props.shouldOpenSearchifyModal,
          }
}

export const OfficialsAndStaffSelect = (props) => {
    const searchifyFLV = OfficialsAndStaffEUFilterableListView

    return GenericPieSelect({
        placeholder: props.multi ? "Choose officials and staff..." : "Choose official and staff...",
        ...props,
        resource: DjangIO.app.person.models.NewPerson,
        advancedSearchifyConfig: generateAdvancedSearchifyConfig(props, searchifyFLV),
        label: OfficialsAndStaffSelect.defaultProps.label,
    })
}
OfficialsAndStaffSelect.defaultProps = { label: "name" }

export const OfficialSelect = (props) => {
    const searchifyFLV = OfficialFilterableListView
    const permanentFilter = getPermanentFilter(props.permanentFilter, {
        most_recent_person_type__in: DjangIO.app.person.types.PersonType.items()
            .filter((val) => val.is_official)
            .map((val) => val.value),
    })

    return GenericPieSelect({
        placeholder: props.multi ? "Choose officials..." : "Choose official...",
        extraFields: ["most_recent_region"],
        ...props,
        resource: DjangIO.app.person.models.NewPerson,
        permanentFilter,
        advancedSearchifyConfig: generateAdvancedSearchifyConfig(props, searchifyFLV, permanentFilter),
        label: OfficialSelect.defaultProps.label,
    })
}
OfficialSelect.defaultProps = { label: "name" }

export const OfficialImpactReportsSelect = (props) => {
    const searchifyFLV = OfficialFilterableListView
    const permanentFilter = getPermanentFilter(props.permanentFilter, {
        most_recent_person_type__in: DjangIO.app.person.types.PersonType.items()
            .filter((val) => val.is_official)
            .map((val) => val.value),
    })

    return GenericPieSelect({
        placeholder: "Choose officials...",
        extraFields: ["most_recent_region"],
        ...props,
        resource: DjangIO.app.person.models.NewPerson,
        permanentFilter,
        withEditSelections: true,
        multi: true,
        serializationKey: "select",
        requiresBaseComponent: false,
        selectAllDisabled: false,
        maxSearchifySelections: 10,
        shouldOpenSearchifyModal: true,
        advancedSearchifyConfig: generateAdvancedSearchifyConfig(props, searchifyFLV, permanentFilter),
        label: OfficialImpactReportsSelect.defaultProps.label,
    })
}
OfficialImpactReportsSelect.defaultProps = { label: "name" }

export const OfficialWithoutCampaignFinanceSelect = (props) => {
    const searchifyFLV = OfficialFilterableWithoutCampaignFinanceListView
    const permanentFilter = getPermanentFilter(props.permanentFilter, {
        most_recent_person_type__in: DjangIO.app.person.types.PersonType.items()
            .filter((val) => val.is_official)
            .map((val) => val.value),
    })

    return GenericPieSelect({
        placeholder: props.multi ? "Choose officials..." : "Choose official...",
        extraFields: ["most_recent_region"],
        ...props,
        resource: DjangIO.app.person.models.NewPerson,
        permanentFilter,
        advancedSearchifyConfig: generateAdvancedSearchifyConfig(props, searchifyFLV, permanentFilter),
        label: OfficialWithoutCampaignFinanceSelect.defaultProps.label,
    })
}
OfficialWithoutCampaignFinanceSelect.defaultProps = { label: "name" }

export const StafferSelect = (props) => {
    const searchifyFLV = StafferFilterableListView
    const permanentFilter = {
        hygiene_staffer_types: true,
        ...(props.permanentFilter || {}),
    }

    return GenericPieSelect({
        placeholder: props.multi ? "Choose staffers..." : "Choose staffer...",
        ...props,
        resource: DjangIO.app.person.models.NewPerson,
        permanentFilter,
        advancedSearchifyConfig: generateAdvancedSearchifyConfig(props, searchifyFLV, permanentFilter),
        label: StafferSelect.defaultProps.label,
        extraFields: ["most_recent_region"],
    })
}
StafferSelect.defaultProps = { label: "name" }

export const StafferImpactReportsSelect = (props) => {
    const searchifyFLV = StafferFilterableListView
    const permanentFilter = {
        hygiene_staffer_types: true,
        ...(props.permanentFilter || {}),
    }

    return GenericPieSelect({
        placeholder: props.multi ? "Choose staffers..." : "Choose staffer...",
        ...props,
        resource: DjangIO.app.person.models.NewPerson,
        permanentFilter,
        advancedSearchifyConfig: generateAdvancedSearchifyConfig(props, searchifyFLV, permanentFilter),
        label: StafferImpactReportsSelect.defaultProps.label,
        extraFields: ["most_recent_region"],
    })
}
StafferImpactReportsSelect.defaultProps = { label: "name" }

export const PressContactSelect = (props) => {
    const searchifyFLV = PressContactFilterableListView
    const permanentFilter = {
        most_recent_person_type: DjangIO.app.person.types.PersonType.press_contact.value,
        ...(props.permanentFilter || {}),
    }

    return GenericPieSelect({
        placeholder: props.multi ? "Choose press contacts..." : "Choose press contact...",
        ...props,
        renderOption: (option) =>
            option.public_organization_descriptor
                ? `${option.name} (${option.public_organization_descriptor})`
                : option.name,
        resource: DjangIO.app.person.models.NewPerson,
        permanentFilter,
        advancedSearchifyConfig: generateAdvancedSearchifyConfig(props, searchifyFLV, permanentFilter),
        label: PressContactSelect.defaultProps.label,
        extraFields: ["public_organization_descriptor"],
    })
}
PressContactSelect.defaultProps = { label: "name" }

export const LegislatorSelect = (props) => {
    const searchifyFLV = LegislatorSearchifyFLV
    const permanentFilter = getPermanentFilter(props.permanentFilter, {
        most_recent_person_type__in: DjangIO.app.person.types.PersonType.member.value,
    })

    return GenericPieSelect({
        placeholder: props.multi ? "Choose legislators..." : "Choose legislator...",
        ...props,
        resource: DjangIO.app.person.models.NewPerson,
        permanentFilter,
        advancedSearchifyConfig: generateAdvancedSearchifyConfig(props, searchifyFLV, permanentFilter),
        label: LegislatorSelect.defaultProps.label,
    })
}
LegislatorSelect.defaultProps = { label: "name" }

export const LegislatorSelectWithoutCampaignFinance = (props) => {
    const searchifyFLV = LegislatorSearchifyWithoutCampaignFinanceFLV
    const permanentFilter = getPermanentFilter(props.permanentFilter, {
        most_recent_person_type__in: DjangIO.app.person.types.PersonType.member.value,
    })

    return GenericPieSelect({
        placeholder: props.multi ? "Choose legislators..." : "Choose legislator...",
        ...props,
        resource: DjangIO.app.person.models.NewPerson,
        permanentFilter,
        advancedSearchifyConfig: generateAdvancedSearchifyConfig(props, searchifyFLV, permanentFilter),
        label: LegislatorSelectWithoutCampaignFinance.defaultProps.label,
    })
}
LegislatorSelectWithoutCampaignFinance.defaultProps = { label: "name" }

export const SupporterCampaignFinanceLegislatorSelect = (props) => {
    const searchifyFLV = SupporterLegislatorSearchifyFLV
    const permanentFilter = getPermanentFilter(props.permanentFilter, {
        most_recent_person_type__in: DjangIO.app.person.types.PersonType.member.value,
        ph_current: true,
        ph_minor_role_type: [
            DjangIO.app.person.newtypes.MinorRoleType.us_senator.value,
            DjangIO.app.person.newtypes.MinorRoleType.us_representative.value,
        ],
    })

    return GenericPieSelect({
        placeholder: props.multi ? "Choose officials..." : "Choose official...",
        ...props,
        resource: DjangIO.app.person.models.NewPerson,
        permanentFilter,
        advancedSearchifyConfig: generateAdvancedSearchifyConfig(props, searchifyFLV, permanentFilter),
        label: SupporterCampaignFinanceLegislatorSelect.defaultProps.label,
    })
}
SupporterCampaignFinanceLegislatorSelect.defaultProps = { label: "name" }

export const PoliticalCommitteeCampaignFinanceLegislatorSelect = (props) => {
    const searchifyFLV = PoliticalCommitteeLegislatorSearchifyFLV
    const permanentFilter = getPermanentFilter(props.permanentFilter, {
        most_recent_person_type__in: DjangIO.app.person.types.PersonType.member.value,
        ph_current: true,
        ph_minor_role_type: [
            DjangIO.app.person.newtypes.MinorRoleType.us_senator.value,
            DjangIO.app.person.newtypes.MinorRoleType.us_representative.value,
        ],
    })

    return GenericPieSelect({
        placeholder: props.multi ? "Choose officials..." : "Choose official...",
        ...props,
        resource: DjangIO.app.person.models.NewPerson,
        permanentFilter,
        advancedSearchifyConfig: generateAdvancedSearchifyConfig(props, searchifyFLV, permanentFilter),
        label: PoliticalCommitteeCampaignFinanceLegislatorSelect.defaultProps.label,
    })
}
PoliticalCommitteeCampaignFinanceLegislatorSelect.defaultProps = { label: "name" }

export const ExecutiveSelect = (props) => {
    const searchifyFLV = ExecutiveSearchifyFLV
    const permanentFilter = getPermanentFilter(props.permanentFilter, {
        most_recent_person_type__in: DjangIO.app.person.types.PersonType.executive.value,
    })

    return GenericPieSelect({
        placeholder: props.multi ? "Choose executives..." : "Choose executive...",
        ...props,
        resource: DjangIO.app.person.models.NewPerson,
        permanentFilter,
        advancedSearchifyConfig: generateAdvancedSearchifyConfig(props, searchifyFLV, permanentFilter),
        label: ExecutiveSelect.defaultProps.label,
    })
}
ExecutiveSelect.defaultProps = { label: "name" }

export const LegSessionSelect = (props) => {
    const searchifyFLV = LegislativeSessionSearchifyFLV

    return GenericPieSelect({
        placeholder: props.multi ? "Choose sessions..." : "Choose session...",
        ...props,
        resource: DjangIO.app.models.LegSession,
        permanentFilter: props.permanentFilter,
        advancedSearchifyConfig: generateAdvancedSearchifyConfig(props, searchifyFLV),
        label: LegSessionSelect.defaultProps.label,
    })
}
LegSessionSelect.defaultProps = { label: "title" }

export const IssueSelect = (props) => {
    const searchifyFLV = IssueSearchifyFLV

    return GenericPieSelect({
        placeholder: props.multi ? "Choose issues..." : "Choose issue...",
        ...props,
        resource: DjangIO.app.bill.models.BillTerm,
        advancedSearchifyConfig: generateAdvancedSearchifyConfig(props, searchifyFLV),
        label: IssueSelect.defaultProps.label,
    })
}
IssueSelect.defaultProps = { label: "name" }

export const CommitteeSelect = (props) => {
    const searchifyFLV = CommitteeFilterableListView

    return GenericPieSelect({
        placeholder: props.multi ? "Choose committees..." : "Choose committee...",
        ...props,
        resource: DjangIO.app.committee.models.Committee,
        advancedSearchifyConfig: generateAdvancedSearchifyConfig(props, searchifyFLV),
        label: CommitteeSelect.defaultProps.label,
        extraFields: ["parent_committee_abbrev"],
        renderOption: (option) =>
            option.parent_committee_abbrev ? `${option.name} (${option.parent_committee_abbrev})` : option.name,
    })
}
CommitteeSelect.defaultProps = { label: "name" }

export const CommitteeImpactReportsSelect = (props) => {
    const searchifyFLV = CommitteeFilterableListView

    return GenericPieSelect({
        placeholder: props.multi ? "Choose committees..." : "Choose committee...",
        ...props,
        resource: DjangIO.app.committee.models.Committee,
        advancedSearchifyConfig: generateAdvancedSearchifyConfig(props, searchifyFLV),
        label: CommitteeImpactReportsSelect.defaultProps.label,
        extraFields: ["parent_committee_abbrev"],
        renderOption: (option) =>
            option.parent_committee_abbrev ? `${option.name} (${option.parent_committee_abbrev})` : option.name,
    })
}
CommitteeImpactReportsSelect.defaultProps = { label: "name" }

export const BillSelect = (props) => {
    const searchifyFLV = BillFilterableListView
    const { bill } = DjangIO.app.models.QuorumDataType

    return GenericPieSelect({
        placeholder: `Choose ${(props.multi ? bill.plural : bill.singular).toLowerCase()}...`,
        ...props,
        resource: DjangIO.app.bill.models.Bill,
        advancedSearchifyConfig: generateAdvancedSearchifyConfig(props, searchifyFLV),
        label: BillSelect.defaultProps.label,
        extraFields: ["region"],
    })
}
BillSelect.defaultProps = { label: "title" }

export const BillImpactReportsSelect = (props) => {
    const searchifyFLV = BillFilterableListView
    const { bill } = DjangIO.app.models.QuorumDataType

    return GenericPieSelect({
        placeholder: `Choose ${(props.multi ? bill.plural : bill.singular).toLowerCase()}...`,
        ...props,
        resource: DjangIO.app.bill.models.Bill,
        advancedSearchifyConfig: generateAdvancedSearchifyConfig(props, searchifyFLV),
        label: BillImpactReportsSelect.defaultProps.label,
        extraFields: ["region"],
    })
}
BillImpactReportsSelect.defaultProps = { label: "title" }

export const ReceiptsCandidateSelect = (props) => {
    const searchifyFLV = CampaignFinanceReceiptsCandidatesFilterableListViewFLVs

    return GenericPieSelect({
        placeholder: `Choose Candidates...`,
        ...props,
        resource: DjangIO.app.moneyball.models.CandidatesSuggester,
        advancedSearchifyConfig: generateAdvancedSearchifyConfig(props, searchifyFLV),
        label: "title",
        extraFields: ["region"],
        filterKey: "candidate_query",
        returnAllResponse: true,
    })
}

export const IndividualContributorEmployerSelect = (props) => {
    const permanentFilter = getPermanentFilter(props.permanentFilter, {
        column_name: "employer",
        default_for_user: false,
    })

    return GenericPieSelect({
        placeholder: `Choose Employers...`,
        ...props,
        resource: DjangIO.app.moneyball.models.IndividualsSuggester,
        label: "title",
        permanentFilter,
        filterKey: "individuals_query",
    })
}

export const IndividualContributorOccupationSelect = (props) => {
    const permanentFilter = getPermanentFilter(props.permanentFilter, {
        column_name: "occupation",
        default_for_user: false,
    })

    return GenericPieSelect({
        placeholder: `Choose Occupations...`,
        ...props,
        resource: DjangIO.app.moneyball.models.IndividualsSuggester,
        label: "title",
        permanentFilter,
        filterKey: "individuals_query",
    })
}

export const IndividualContributorCitySelect = (props) => {
    const permanentFilter = getPermanentFilter(props.permanentFilter, {
        column_name: "city",
        default_for_user: false,
    })

    return GenericPieSelect({
        placeholder: `Choose Cities...`,
        ...props,
        resource: DjangIO.app.moneyball.models.IndividualsSuggester,
        label: "title",
        permanentFilter,
        filterKey: "individuals_query",
    })
}

export const IndividualContributorZipCodeSelect = (props) => {
    const permanentFilter = getPermanentFilter(props.permanentFilter, {
        column_name: "zipcode",
        default_for_user: false,
    })

    return GenericPieSelect({
        placeholder: `Choose Zip Codes...`,
        ...props,
        resource: DjangIO.app.moneyball.models.IndividualsSuggester,
        label: "title",
        permanentFilter,
        filterKey: "individuals_query",
    })
}

export const IndividualContributorStateSelect = (props) => {
    return genericEnumSelect({
        ...props,
        options: DjangIO.app.models.Region.items()
            .filter((item) => item.is_state_region)
            .map((region) => ({ value: region.abbrev.toUpperCase(), label: region.region_name })),
        placeholder: props.multi ? "Choose States..." : "Select State",
        label: "label",
    })
}

export const DisburserSelect = (props) => {
    const searchifyFLV = CampaignFinanceDisbursementsFilterableListViewFLVs

    return GenericPieSelect({
        placeholder: `Choose Political Сommittees...`,
        ...props,
        resource: DjangIO.app.moneyball.models.CommitteesSuggester,
        advancedSearchifyConfig: generateAdvancedSearchifyConfig(props, searchifyFLV),
        label: DisburserSelect.defaultProps.label,
        extraFields: ["region"],
        filterKey: "disburser_query",
        returnAllResponse: true,
    })
}
DisburserSelect.defaultProps = { label: "title" }

export const VoteSelect = (props) => {
    const searchifyFLV = VoteFilterableListView

    return GenericPieSelect({
        placeholder: props.multi ? "Choose votes..." : "Choose vote...",
        ...props,
        resource: DjangIO.app.vote.models.QuickVote,
        advancedSearchifyConfig: generateAdvancedSearchifyConfig(props, searchifyFLV),
        label: VoteSelect.defaultProps.label,
    })
}
VoteSelect.defaultProps = { label: "question" }

export const VoteResultSelect = (props) => {
    const searchifyFLV = VoteResultFilterableListView

    return GenericPieSelect({
        placeholder: props.multi ? "Choose votes..." : "Choose vote...",
        ...props,
        resource: DjangIO.app.vote.models.VoteResult,
        advancedSearchifyConfig: generateAdvancedSearchifyConfig(props, searchifyFLV),
        label: VoteResultSelect.defaultProps.label,
    })
}
VoteResultSelect.defaultProps = { label: "vote" }

export const AmendmentSelect = (props) => {
    const searchifyFLV = AmendmentFilterableListView

    return GenericPieSelect({
        placeholder: props.multi ? "Choose amendments..." : "Choose amendment...",
        ...props,
        resource: DjangIO.app.bill.models.Amendment,
        advancedSearchifyConfig: generateAdvancedSearchifyConfig(props, searchifyFLV),
        label: AmendmentSelect.defaultProps.label,
    })
}
AmendmentSelect.defaultProps = { label: "title" }

export const CaucusSelect = (props) => {
    const searchifyFLV = CaucusFilterableListView

    return GenericPieSelect({
        placeholder: props.multi ? "Choose caucuses..." : "Choose caucus...",
        ...props,
        resource: DjangIO.app.committee.models.Caucus,
        advancedSearchifyConfig: generateAdvancedSearchifyConfig(props, searchifyFLV),
        label: CaucusSelect.defaultProps.label,
    })
}
CaucusSelect.defaultProps = { label: "name" }

export const CaucusImpactReportSelect = (props) => {
    const searchifyFLV = CaucusFilterableListView

    return GenericPieSelect({
        placeholder: props.multi ? "Choose caucuses..." : "Choose caucus...",
        ...props,
        resource: DjangIO.app.committee.models.Caucus,
        advancedSearchifyConfig: generateAdvancedSearchifyConfig(props, searchifyFLV),
        label: CaucusSelect.defaultProps.label,
    })
}
CaucusSelect.defaultProps = { label: "name" }

export const CustomGeoDataSelect = (props) => {
    const permanentFilter = getPermanentFilter(props.permanentFilter, { is_batch_data: false })

    return GenericPieSelect({
        placeholder: props.multi ? "Choose custom geographic datasets..." : "Choose custom geographic dataset...",
        ...props,
        resource: DjangIO.app.custom_data.manage_data.models.CustomData,
        permanentFilter,
        label: CustomGeoDataSelect.defaultProps.label,
    })
}
CustomGeoDataSelect.defaultProps = { label: "category" }

export const StafferTitleSelect = (props) => {
    const searchifyFLV = StafferTitleSearchifyFLV
    const permanentFilter = getPermanentFilter(props.permanentFilter, {
        limit: 0,
        has_people: true,
    })

    return GenericPieSelect({
        placeholder: props.multi ? "Choose role types..." : "Choose role type...",
        ...props,
        resource: DjangIO.app.person.models.StaffTitle,
        permanentFilter,
        advancedSearchifyConfig: generateAdvancedSearchifyConfig(props, searchifyFLV, permanentFilter),
        label: StafferTitleSelect.defaultProps.label,
    })
}
StafferTitleSelect.defaultProps = { label: "title" }

export const StafferTermSelect = (props) => {
    const searchifyFLV = StafferTermSearchifyFLV
    const permanentFilter = getPermanentFilter(props.permanentFilter, {
        limit: 0,
        has_people: true,
    })

    return GenericPieSelect({
        placeholder: props.multi ? "Choose issues..." : "Choose issue...",
        ...props,
        resource: DjangIO.app.person.models.StafferTerm,
        permanentFilter,
        advancedSearchifyConfig: generateAdvancedSearchifyConfig(props, searchifyFLV, permanentFilter),
        label: StafferTermSelect.defaultProps.label,
    })
}
StafferTermSelect.defaultProps = { label: "kw_responsibility" }

export const StateSelect = (props) => {
    const searchifyFLV = StateSearchifyFLV

    return GenericPieSelect({
        placeholder:
            props.placeholder || (props.multi ? "Choose states or territories..." : "Choose state or territory..."),
        ...props,
        resource: DjangIO.app.geography.models.State,
        advancedSearchifyConfig: generateAdvancedSearchifyConfig(props, searchifyFLV),
        label: StateSelect.defaultProps.label,
    })
}
StateSelect.defaultProps = { label: "name" }

export const UpperDistrictSelect = (props) => {
    const searchifyFLV = UpperLDSearchifyFLV

    return GenericPieSelect({
        placeholder: props.multi ? "Choose upper state districts" : "Choose upper state district...",
        ...props,
        resource: DjangIO.app.geography.models.UpperLD,
        advancedSearchifyConfig: generateAdvancedSearchifyConfig(props, searchifyFLV),
        label: UpperDistrictSelect.defaultProps.label,
    })
}
UpperDistrictSelect.defaultProps = { label: "kw_Description" }

export const LowerDistrictSelect = (props) => {
    const searchifyFLV = LowerLDSearchifyFLV

    return GenericPieSelect({
        placeholder: props.multi ? "Choose lower state districts" : "Choose lower state district...",
        ...props,
        resource: DjangIO.app.geography.models.LowerLD,
        advancedSearchifyConfig: generateAdvancedSearchifyConfig(props, searchifyFLV),
        label: LowerDistrictSelect.defaultProps.label,
    })
}
LowerDistrictSelect.defaultProps = { label: "kw_Description" }

export const CountySelect = (props) => {
    const searchifyFLV = CountySearchifyFLV

    return GenericPieSelect({
        placeholder: props.multi ? "Choose counties..." : "Choose county...",
        ...props,
        resource: DjangIO.app.geography.models.County,
        advancedSearchifyConfig: generateAdvancedSearchifyConfig(props, searchifyFLV),
        label: CountySelect.defaultProps.label,
    })
}
CountySelect.defaultProps = { label: "name" }

export const CountrySelect = (props) => {
    const searchifyFLV = CountrySearchifyFLV

    return GenericPieSelect({
        placeholder: props.multi ? "Choose countries..." : "Choose country...",
        ...props,
        resource: DjangIO.app.geography.models.Country,
        advancedSearchifyConfig: generateAdvancedSearchifyConfig(props, searchifyFLV),
        label: CountrySelect.defaultProps.label,
    })
}
CountrySelect.defaultProps = { label: "name" }

export const CountryEUSelect = (props) => {
    const searchifyFLV = CountrySearchifyFLV
    const permanentFilter = getPermanentFilter(props.permanentFilter, {
        only_eu_countries: true,
    })

    return GenericPieSelect({
        placeholder: props.multi ? "Choose EU Member States..." : "Choose EU Member State...",
        ...props,
        resource: DjangIO.app.geography.models.Country,
        permanentFilter,
        advancedSearchifyConfig: generateAdvancedSearchifyConfig(props, searchifyFLV),
        label: CountryEUSelect.defaultProps.label,
    })
}
CountryEUSelect.defaultProps = { label: "name" }

export const CanadianFederalElectoralDistrictSelect = (props) => {
    const searchifyFLV = CanadianFederalElectoralDistrictSearchifyFLV
    const permanentFilter = getPermanentFilter(props.permanentFilter, {
        geo_shape_type: DjangIO.app.geography.types.GeoShapeType.canadian_federal_electoral_district.value,
        order_by: "name",
    })

    return GenericPieSelect({
        placeholder: props.multi ? "Choose federal electoral districts..." : "Choose federal electoral district...",
        ...props,
        resource: DjangIO.app.geography.models.GeoShape,
        permanentFilter,
        advancedSearchifyConfig: generateAdvancedSearchifyConfig(props, searchifyFLV, permanentFilter),
        label: CanadianFederalElectoralDistrictSelect.defaultProps.label,
    })
}
CanadianFederalElectoralDistrictSelect.defaultProps = { label: "name" }

export const MunicipalitySelect = (props) => {
    const searchifyFLV = MunicipalitySearchifyFLV

    return GenericPieSelect({
        placeholder: props.multi ? "Choose towns or cities..." : "Choose town or city",
        ...props,
        resource: DjangIO.app.geography.models.Municipality,
        advancedSearchifyConfig: generateAdvancedSearchifyConfig(props, searchifyFLV),
        label: MunicipalitySelect.defaultProps.label,
    })
}
MunicipalitySelect.defaultProps = { label: "name" }

export const DistrictSelect = (props) => {
    const searchifyFLV = DistrictSearchifyFLV

    return GenericPieSelect({
        placeholder: props.multi ? "Choose congressional districts..." : "Choose congressional district...",
        ...props,
        resource: DjangIO.app.geography.models.District,
        advancedSearchifyConfig: generateAdvancedSearchifyConfig(props, searchifyFLV),
        label: DistrictSelect.defaultProps.label,
    })
}
DistrictSelect.defaultProps = { label: "name" }

export const GeoShapeSelect = (props) => {
    const searchifyFLV = GeoShapeSearchifyFLV
    const permanentFilter = getPermanentFilter(props.permanentFilter, { filter_region_for_dlc: true })

    return GenericPieSelect({
        placeholder: props.multi ? "Choose geographies..." : "Choose geography...",
        ...props,
        resource: DjangIO.app.geography.models.GeoShape,
        permanentFilter,
        advancedSearchifyConfig: generateAdvancedSearchifyConfig(props, searchifyFLV),
        label: GeoShapeSelect.defaultProps.label,
    })
}
GeoShapeSelect.defaultProps = { label: "name" }

export const CustomFieldGroupSelect = (props) => {
    return GenericPieSelect({
        placeholder: "Choose group...",
        ...props,
        resource: DjangIO.app.custom_field.models.CustomFieldGroup,
        label: CustomFieldGroupSelect.defaultProps.label,
    })
}
CustomFieldGroupSelect.defaultProps = { label: "name" }

export const UserSelect = (props) => {
    const searchifyFLV = UserFilterableListView

    return GenericPieSelect({
        placeholder: props.multi ? "Choose team members..." : "Choose team member...",
        allowExpandUp: true,
        ...props,
        resource: DjangIO.app.userdata.models.QuorumUser,
        advancedSearchifyConfig: generateAdvancedSearchifyConfig(props, searchifyFLV),
        label: UserSelect.defaultProps.label,
    })
}
UserSelect.defaultProps = { label: "name" }

export const BulkSMSCreatorSelect = (props) => {
    const searchifyFLV = UserFilterableListView
    return GenericPieSelect({
        placeholder: props.multi ? "Choose creator" : "Choose creators...",
        ...props,
        resource: DjangIO.app.userdata.models.QuorumUser,
        advancedSearchifyConfig: generateAdvancedSearchifyConfig(props, searchifyFLV),
        label: UserSelect.defaultProps.label,
    })
}
BulkSMSCreatorSelect.defaultProps = { label: "name" }

export const TeamSelect = (props) => {
    const searchifyFLV = TeamSearchifyFLV

    return GenericPieSelect({
        placeholder: props.multi ? "Choose teams..." : "Choose team...",
        ...props,
        resource: DjangIO.app.userdata.models.Team,
        advancedSearchifyConfig: generateAdvancedSearchifyConfig(props, searchifyFLV),
        label: TeamSelect.defaultProps.label,
        shouldBeUniqueId: true,
    })
}
TeamSelect.defaultProps = { label: "name" }

export const OrganizationSelect = (props) => {
    const searchifyFLV = OrganizationSearchifyFLV

    return GenericPieSelect({
        placeholder: props.multi ? "Choose organizations..." : "Choose organization...",
        ...props,
        resource: DjangIO.app.userdata.models.Organization,
        advancedSearchifyConfig: generateAdvancedSearchifyConfig(props, searchifyFLV),
        label: OrganizationSelect.defaultProps.label,
    })
}
OrganizationSelect.defaultProps = { label: "name" }

export const OrganizationImpactReportsSelect = (props) => {
    const searchifyFLV = OrganizationSearchifyFLV

    return GenericPieSelect({
        placeholder: props.multi ? "Choose organizations..." : "Choose organization...",
        ...props,
        resource: DjangIO.app.userdata.models.Organization,
        advancedSearchifyConfig: generateAdvancedSearchifyConfig(props, searchifyFLV),
        label: OrganizationImpactReportsSelect.defaultProps.label,
    })
}
OrganizationImpactReportsSelect.defaultProps = { label: "name" }

export const OutletSelect = (props) => {
    const searchifyFLV = PublicOrganizationFilterableListView
    const permanentFilter = getPermanentFilter(props.permanentFilter, {
        _ph_sector_type: DjangIO.app.person.newtypes.SectorType.media.value,
    })

    return GenericPieSelect({
        placeholder: props.multi ? "Choose outlets..." : "Choose outlet...",
        ...props,
        resource: DjangIO.app.person.models.PublicOrganization,
        permanentFilter,
        advancedSearchifyConfig: generateAdvancedSearchifyConfig(props, searchifyFLV, permanentFilter),
        label: OutletSelect.defaultProps.label,
    })
}
OutletSelect.defaultProps = { label: "name_with_parent" }

export const PublicOrganizationSelect = (props) => {
    const searchifyFLV = PublicOrganizationFilterableListView

    return GenericPieSelect({
        placeholder: props.multi ? "Choose organizations..." : "Choose organization...",
        ...props,
        resource: DjangIO.app.person.models.PublicOrganization,
        advancedSearchifyConfig: generateAdvancedSearchifyConfig(props, searchifyFLV),
        label: PublicOrganizationSelect.defaultProps.label,
    })
}
PublicOrganizationSelect.defaultProps = { label: "name_with_parent" }

export const PublicPacOrganizationSelect = (props) => {
    const searchifyFLV = PublicOrganizationFilterableListView
    const permanentFilter = getPermanentFilter(props.permanentFilter, { pac_org_type__isnull: false })
    const label =
        props.customProps && props.customProps.label
            ? props.customProps.label
            : PublicPacOrganizationSelect.defaultProps.label

    return GenericPieSelect({
        advancedSearchifyConfig: generateAdvancedSearchifyConfig(props, searchifyFLV, permanentFilter),
        permanentFilter,
        placeholder: props.multi ? "Choose PAC organizations..." : "Choose PAC organization...",
        resource: DjangIO.app.person.models.PublicOrganization,
        shouldBeUniqueId: true,
        ...props,
        label,
    })
}
PublicPacOrganizationSelect.defaultProps = { label: "name_with_parent" }

export const PublicNonPacOrganizationSelect = (props) => {
    const searchifyFLV = PublicOrganizationFilterableListView
    const permanentFilter = getPermanentFilter(props.permanentFilter, {
        organization__isnull: false,
        pac_org_type__isnull: true,
    })
    const label =
        props.customProps && props.customProps.label
            ? props.customProps.label
            : PublicPacOrganizationSelect.defaultProps.label

    return GenericPieSelect({
        advancedSearchifyConfig: generateAdvancedSearchifyConfig(props, searchifyFLV, permanentFilter),
        permanentFilter,
        placeholder: props.multi ? "Choose organizations..." : "Choose organization...",
        resource: DjangIO.app.person.models.PublicOrganization,
        shouldBeUniqueId: true,
        ...props,
        label,
    })
}
PublicNonPacOrganizationSelect.defaultProps = { label: "name_with_parent" }

export const PublicOrganizationPartySelect = (props) => {
    const searchifyFLV = PublicOrganizationFilterableListView

    return GenericPieSelect({
        placeholder: props.multi ? "Choose parties..." : "Choose party...",
        ...props,
        resource: DjangIO.app.person.models.PublicOrganization,
        advancedSearchifyConfig: generateAdvancedSearchifyConfig(props, searchifyFLV),
        label: PublicOrganizationPartySelect.defaultProps.label,
    })
}
PublicOrganizationPartySelect.defaultProps = { label: "name_with_parent" }

export const PublicOrganizationNonCustomSelect = (props) => {
    const searchifyFLV = PublicOrganizationFilterableListView
    const permanentFilter = getPermanentFilter(props.permanentFilter, { non_custom_only: true })

    return GenericPieSelect({
        placeholder: props.multi ? "Choose organizations..." : "Choose organization...",
        ...props,
        resource: DjangIO.app.person.models.PublicOrganization,
        permanentFilter,
        advancedSearchifyConfig: generateAdvancedSearchifyConfig(props, searchifyFLV, permanentFilter),
        label: PublicOrganizationNonCustomSelect.defaultProps.label,
    })
}
PublicOrganizationNonCustomSelect.defaultProps = { label: "name_with_parent" }

export const ListSelect = (props) => {
    const permanentFilter = Object.assign(
        {
            dehydrate_extra: ["project_name"],
        },
        props.permanentFilter || {},
    )
    const renderOption = (option) =>
        option._extra && option._extra.project_name ? `${option.name} (${option._extra.project_name})` : option.name

    return GenericPieSelect({
        placeholder: props.multi ? "Choose lists..." : "Choose list...",
        ...props,
        permanentFilter,
        renderOption,
        resource: DjangIO.app.userdata.models.NewList,
        advancedSearchifyConfig: generateAdvancedSearchifyConfig(props, NewListFilterableListView),
        label: ListSelect.defaultProps.label,
        returnAllResponse: true,
    })
}
ListSelect.defaultProps = { label: "name" }

export const CampaignSelect = (props) => {
    const searchifyFLV = CampaignFilterableListView
    const permanentFilter = {
        activated__isnull: false,
        archived: false,
        order_by: "action_center__name",
        ...props.permanentFilter,
    }

    return GenericPieSelect({
        placeholder: props.multi ? "Choose campaigns..." : "Choose campaign...",
        ...props,
        resource: DjangIO.app.grassroots.campaign.models.Campaign,
        permanentFilter,
        advancedSearchifyConfig: generateAdvancedSearchifyConfig(props, searchifyFLV, permanentFilter),
    })
}
CampaignSelect.defaultProps = {
    label: Userdata.has_singular_action_center ? "name" : "powersearch_label",
}

export const ActiveCampaignSelect = (props) => {
    const permanentFilter = getPermanentFilter(props.permanentFilter, {
        status__in: [
            DjangIO.app.grassroots.campaign.types.CampaignStatus.active.value,
            DjangIO.app.grassroots.campaign.types.CampaignStatus.complete.value,
        ],
    })

    return GenericPieSelect({
        placeholder: props.multi ? "Choose campaigns..." : "Choose campaign...",
        ...props,
        resource: DjangIO.app.grassroots.campaign.models.Campaign,
        permanentFilter,
        label: ActiveCampaignSelect.defaultProps.label,
    })
}
ActiveCampaignSelect.defaultProps = { label: "name" }

export const AvailableForCombinedCampaignSelect = (props) => {
    const { CampaignType, CampaignStatus } = DjangIO.app.grassroots.campaign.types

    const permanentFilter = {
        ...props.permanentFilter,
        status__in: [CampaignStatus.draft.value, CampaignStatus.active.value],
        campaign_type__in: CampaignType.items()
            .filter((item) => item.in_combined_campaign)
            .map((item) => item.value),
        name__gt: "",
    }

    return GenericPieSelect({
        placeholder: props.multi ? "Choose campaigns..." : "Choose campaign...",
        ...props,
        resource: DjangIO.app.grassroots.campaign.models.Campaign,
        permanentFilter,
        label: props?.label ?? AvailableForCombinedCampaignSelect.defaultProps.label,
    })
}

AvailableForCombinedCampaignSelect.defaultProps = { label: "name" }

export const ActionCenterSettingsSelect = (props) => {
    const permanentFilter = getPermanentFilter(props.permanentFilter, { archived: false })

    return GenericPieSelect({
        placeholder: props.multi ? "Choose action centers..." : "Choose action center...",
        ...props,
        resource: DjangIO.app.grassroots.models.ActionCenterSettings,
        permanentFilter,
        label: ActionCenterSettingsSelect.defaultProps.label,
    })
}
ActionCenterSettingsSelect.defaultProps = { label: "name" }

export const GrassrootsActionCenterSettingsSelect = (props) => {
    const permanentFilter = getPermanentFilter(props.permanentFilter, { archived: false })

    return GenericPieSelect({
        placeholder: props.multi ? "Choose action centers..." : "Choose action center...",
        ...props,
        resource: DjangIO.app.grassroots.models.GrassrootsActionCenterSettings,
        permanentFilter,
        label: GrassrootsActionCenterSettingsSelect.defaultProps.label,
    })
}
GrassrootsActionCenterSettingsSelect.defaultProps = { label: "name" }

export const GrassrootsRegistrationPageSelect = (props) => {
    const permanentFilter = {
        archived: false,
        grassroots_form_type: DjangIO.app.grassroots.types.GrassrootsFormType.registration.value,
        ...props.permanentFilter,
    }

    return GenericPieSelect({
        placeholder: props.multi ? "Choose registration forms..." : "Choose registration forms...",
        ...props,
        resource: DjangIO.app.grassroots.models.GrassrootsRegistrationPage,
        permanentFilter,
        label: GrassrootsRegistrationPageSelect.defaultProps.label,
    })
}
GrassrootsRegistrationPageSelect.defaultProps = { label: "name" }

export const GrassrootsDonationFormSelect = (props) => {
    const permanentFilter = {
        archived: false,
        grassroots_form_type: DjangIO.app.grassroots.types.GrassrootsFormType.donation.value,
        ...props.permanentFilter,
    }

    return GenericPieSelect({
        placeholder: props.multi ? "Choose donation forms..." : "Choose donation form...",
        ...props,
        resource: DjangIO.app.grassroots.models.GrassrootsRegistrationPage,
        permanentFilter,
        label: GrassrootsRegistrationPageSelect.defaultProps.label,
    })
}
GrassrootsDonationFormSelect.defaultProps = { label: "name" }

export const GrassrootsPageSelect = (props) => {
    const permanentFilter = getPermanentFilter(props.permanentFilter, { archived: false })

    return GenericPieSelect({
        placeholder: props.multi ? "Choose pages..." : "Choose page...",
        ...props,
        resource: DjangIO.app.grassroots.models.QuorumGrassrootsPage,
        permanentFilter,
        label: GrassrootsPageSelect.defaultProps.label,
    })
}
GrassrootsPageSelect.defaultProps = { label: "name" }

export const SheetSelect = (props) => {
    const permanentFilter = getPermanentFilter(props.permanentFilter, { archived: false })

    return GenericPieSelect({
        placeholder: props.multi ? "Choose sheets..." : "Choose sheet...",
        resource: DjangIO.app.userdata.spreadsheet.models.Sheet,
        ...props,
        permanentFilter,
        extraFields: ["public_slug"],
        label: SheetSelect.defaultProps.label,
    })
}
SheetSelect.defaultProps = { label: "name" }

export const EventSelect = (props) => {
    const searchifyFLV = EventFilterableListView

    return GenericPieSelect({
        placeholder: props.multi ? "Choose official events..." : "Choose official event...",
        ...props,
        resource: DjangIO.app.events.models.Event,
        advancedSearchifyConfig: generateAdvancedSearchifyConfig(props, searchifyFLV),
        label: EventSelect.defaultProps.label,
    })
}
EventSelect.defaultProps = { label: "subject" }

export const HearingSelect = (props) => {
    const searchifyFLV = EventFilterableListView

    return GenericPieSelect({
        placeholder: props.multi ? "Choose hearings..." : "Choose hearing...",
        ...props,
        resource: DjangIO.app.events.models.Event,
        advancedSearchifyConfig: generateAdvancedSearchifyConfig(props, searchifyFLV),
        label: HearingSelect.defaultProps.label,
    })
}
HearingSelect.defaultProps = { label: "subject" }

export const SponsorSelect = (props) => {
    const searchifyFLV = OfficialFilterableListView
    const permanentFilter = getPermanentFilter(props.permanentFilter, {
        most_recent_person_type__in: [
            DjangIO.app.person.types.PersonType.member.value,
            DjangIO.app.person.types.PersonType.local_member.value,
            DjangIO.app.person.types.PersonType.executive.value,
        ],
        ph_current: true,
    })

    return GenericPieSelect({
        placeholder: props.multi ? "Choose sponsors..." : "Choose sponsor...",
        extraFields: ["most_recent_region"],
        ...props,
        resource: DjangIO.app.person.models.NewPerson,
        permanentFilter,
        advancedSearchifyConfig: generateAdvancedSearchifyConfig(props, searchifyFLV, permanentFilter),
        label: SponsorSelect.defaultProps.label,
    })
}
SponsorSelect.defaultProps = { label: "name" }

export const ContactSelect = (props) => {
    const searchifyFLV = SupporterFilterableListView
    const label =
        props.customProps && props.customProps.label
            ? // this can be rewritten as one-liner using optional chaining syntax
              // props.customProps?.label
              props.customProps.label
            : ContactSelect.defaultProps.label

    return GenericPieSelect({
        placeholder: props.multi ? "Choose contacts..." : "Choose contact...",
        allowExpandUp: true,
        ...props,
        renderOption: (option) => {
            const optionLabel = [option[label]]

            if (option.email) {
                optionLabel.push(`<${option.email}>`)
            }

            if (option.public_organization_name) {
                optionLabel.push(`(${option.public_organization_name})`)
            }

            return optionLabel.join(" ")
        },
        resource: DjangIO.app.grassroots.models.Supporter,
        advancedSearchifyConfig: generateAdvancedSearchifyConfig(props, searchifyFLV),
        label,
        extraFields: ["email", "public_organization_name", "_extra", "region", "public_organizations"],
    })
}
ContactSelect.defaultProps = { label: "name" }

export const PacContactSelect = (props) => {
    const permanentFilter = getPermanentFilter(props.permanentFilter, { archived: false })

    return GenericPieSelect({
        ...props,
        permanentFilter,
        renderOption: (option) => {
            const lastname = option["lastname"]
            const firstname = option["firstname"]
            const email = option["email"]

            return [lastname, firstname, email].filter((item) => item).join(", ")
        },
        resource: DjangIO.app.grassroots.models.Supporter,
        extraFields: ["lastname", "firstname", "email", "_extra"],
    })
}

export const ParentIssueSelect = (props) => {
    const searchifyFLV = IssueManagementFilterableListView

    return GenericPieSelect({
        placeholder: props.multi ? "Choose issues..." : "Choose issue...",
        ...props,
        resource: DjangIO.app.userdata.models.IssueManagement,
        advancedSearchifyConfig: generateAdvancedSearchifyConfig(props, searchifyFLV, props.permanentFilter),
        label: ParentIssueSelect.defaultProps.label,
    })
}
ParentIssueSelect.defaultProps = { label: "name" }

export const SuperuserProjectSelect = (props) => {
    return GenericPieSelect({
        placeholder: props.multi ? "Choose projects..." : "Choose project...",
        ...props,
        resource: DjangIO.app.userdata.models.Project,
        label: SuperuserProjectSelect.defaultProps.label,
    })
}
SuperuserProjectSelect.defaultProps = { label: "name" }

export const IssueManagementSelect = (props) => {
    const searchifyFLV = IssueManagementFilterableListView
    const permanentFilter = Object.assign(
        {
            dehydrate_extra: ["parent_name"],
        },
        props.permanentFilter || {},
    )
    const renderOption = (option) => {
        return option.parent && option._extra.parent_name
            ? `${option.name} (${option._extra.parent_name})`
            : option.name
    }

    return GenericPieSelect({
        allowExpandUp: props.allowExpandUp,
        placeholder: props.multi ? "Choose issues..." : "Choose issue...",
        ...props,
        renderOption,
        permanentFilter,
        resource: DjangIO.app.userdata.models.IssueManagement,
        advancedSearchifyConfig: generateAdvancedSearchifyConfig(props, searchifyFLV, permanentFilter),
        label: IssueManagementSelect.defaultProps.label,
        extraFields: ["parent", "_extra"],
    })
}
IssueManagementSelect.defaultProps = { label: "name" }

export const EmailTemplateSelect = (props) => {
    return GenericPieSelect({
        placeholder: "Choose an email template",
        ...props,
        label: "name",
        resource: DjangIO.app.userdata.outbox.models.email_template.EmailTemplate,
        multi: false,
    })
}

export const OrganizationDesignSelect = (props) => {
    return GenericPieSelect({
        placeholder: props.multi ? "Choose organization designs..." : "Choose organization design...",
        ...props,
        resource: DjangIO.app.userdata.models.OrganizationDesign,
        multi: props.multi,
        label: OrganizationDesignSelect.defaultProps.label,
    })
}
OrganizationDesignSelect.defaultProps = { label: "design_name" }

export const WitnessSelect = (props) => {
    return GenericPieSelect({
        placeholder: props.multi ? "Choose witnesses..." : "Choose witness...",
        ...props,
        resource: DjangIO.app.committee.models.CommitteeWitness,
        label: WitnessSelect.defaultProps.label,
    })
}
WitnessSelect.defaultProps = { label: "name" }

export const NoteSelect = (props) => {
    const searchifyFLV = NoteFilterableListView
    const permanentFilter = getPermanentFilter(props.permanentFilter, { plain: true })

    return GenericPieSelect({
        placeholder: props.multi ? "Choose notes..." : "Choose note...",
        ...props,
        resource: DjangIO.app.userdata.notes.models.Note,
        permanentFilter,
        advancedSearchifyConfig: generateAdvancedSearchifyConfig(props, searchifyFLV, permanentFilter),
        label: NoteSelect.defaultProps.label,
    })
}
NoteSelect.defaultProps = { label: "humanized_text" }

export const NoteImpactReportsSelect = (props) => {
    const searchifyFLV = NoteFilterableListView
    const permanentFilter = getPermanentFilter(props.permanentFilter, { plain: true })

    return GenericPieSelect({
        placeholder: props.multi ? "Choose notes..." : "Choose note...",
        ...props,
        resource: DjangIO.app.userdata.notes.models.Note,
        permanentFilter,
        advancedSearchifyConfig: generateAdvancedSearchifyConfig(props, searchifyFLV, permanentFilter),
        label: NoteImpactReportsSelect.defaultProps.label,
    })
}
NoteImpactReportsSelect.defaultProps = { label: "humanized_text" }

export const GrassrootsActionSelect = (props) => {
    const searchifyFLV = GrassrootsActionFilterableListView
    const permanentFilter = getPermanentFilter(props.permanentFilter, { plain: true })

    return GenericPieSelect({
        placeholder: props.multi ? "Choose actions..." : "Choose action...",
        ...props,
        resource: DjangIO.app.grassroots.models.GrassrootsSupporterAction,
        advancedSearchifyConfig: generateAdvancedSearchifyConfig(props, searchifyFLV),
        permanentFilter,
        label: GrassrootsActionSelect.defaultProps.label,
    })
}
GrassrootsActionSelect.defaultProps = {
    defaultAdvancedSearchifyConfig: {
        quorumDataType: DjangIO.app.models.QuorumDataType.grassroots_action.value,
        maxSearchifySelections: 50000,
    },
    label: "humanized_text",
}

export const CustomTargetSelect = (props) => {
    const searchifyFLV = GrassrootsActionFilterableListView
    const permanentFilter = getPermanentFilter(props.permanentFilter, {
        plain: true,
        group_by_custom_targets: true,
    })
    return GenericPieSelect({
        placeholder: props.multi ? "Choose emails..." : "Choose email...",
        ...props,
        resource: DjangIO.app.grassroots.models.GrassrootsSupporterAction,
        advancedSearchifyConfig: generateAdvancedSearchifyConfig(props, searchifyFLV),
        permanentFilter,
        label: CustomTargetSelect.defaultProps.label,
        shouldBeUniqueId: true,
    })
}
CustomTargetSelect.defaultProps = {
    defaultAdvancedSearchifyConfig: {
        quorumDataType: DjangIO.app.models.QuorumDataType.grassroots_action.value,
        maxSearchifySelections: 50000,
    },
    label: "custom_email_target",
}

export const OfficeSelect = (props) => {
    const searchifyFLV = OfficialFilterableWithoutCampaignFinanceListView
    const permanentFilter = getPermanentFilter(props.permanentFilter, {
        most_recent_person_type__in: DjangIO.app.person.types.PersonType.items()
            .filter((val) => val.is_official)
            .map((val) => val.value),
    })

    return GenericPieSelect({
        placeholder: props.multi ? "Choose offices..." : "Choose office...",
        ...props,
        resource: DjangIO.app.person.models.NewPerson,
        permanentFilter,
        advancedSearchifyConfig: generateAdvancedSearchifyConfig(props, searchifyFLV),
        label: OfficeSelect.defaultProps.label,
    })
}
OfficeSelect.defaultProps = { label: "name" }

export const LegislativeStatCategorySelect = (props) => {
    const permanentFilter = getPermanentFilter(props.permanentFilter, { is_legislative: "" })
    return GenericPieSelect({
        placeholder: props.multi ? "Choose Legislative Stat Categories" : "Choose Legislative Stat Category",
        ...props,
        resource: DjangIO.app.stats.models.StatCategory,
        permanentFilter,
        label: LegislativeStatCategorySelect.defaultProps.label,
    })
}
LegislativeStatCategorySelect.defaultProps = { label: "full_name" }

export const DemographicStatCategorySelect = (props) => {
    const permanentFilter = getPermanentFilter(props.permanentFilter, { is_demographic: "" })

    return GenericPieSelect({
        placeholder: props.multi ? "Choose Demographic Stat Categories" : "Choose Demographic Stat Category",
        ...props,
        resource: DjangIO.app.stats.models.StatCategory,
        permanentFilter,
        label: DemographicStatCategorySelect.defaultProps.label,
    })
}
DemographicStatCategorySelect.defaultProps = { label: "full_name" }

export const BulkEmailSelect = (props) => {
    const searchifyFLV = BulkEmailFilterableListView
    const permanentFilter = getPermanentFilter(props.permanentFilter, {
        is_draft: false,
        dehydrate_extra: ["abtest_description"],
        excludes_truncation: true,
    })

    const renderOption = (option) =>
        option && option.is_abtest ? getNestedObjectKey(option, ["_extra", "abtest_description"]) : option.subject

    return GenericPieSelect({
        placeholder: props.multi
            ? "Choose Outbox emails sent in the last 12 months..."
            : "Choose Outbox email sent in the last 12 months...",
        ...props,
        resource: DjangIO.app.userdata.outbox.models.bulk_email.BulkEmail,
        extraFields: ["_extra", "is_abtest", "subject"],
        renderOption,
        permanentFilter,
        advancedSearchifyConfig: generateAdvancedSearchifyConfig(props, searchifyFLV, permanentFilter),
        label: BulkEmailSelect.defaultProps.label,
    })
}
BulkEmailSelect.defaultProps = { label: "subject" }

export const BulkSMSSelect = (props) => {
    const searchifyFLV = BulkSMSFilterableListView

    const permanentFilter = getPermanentFilter(props.permanentFilter, { exclude_drafts: true })

    return GenericPieSelect({
        placeholder: "Choose Outbound Texts...",
        ...props,
        resource: DjangIO.app.texting.models.BulkSMS,
        permanentFilter,
        advancedSearchifyConfig: generateAdvancedSearchifyConfig(props, searchifyFLV, permanentFilter),
        label: BulkSMSSelect.defaultProps.label,
    })
}
BulkSMSSelect.defaultProps = { label: "description" }

export const DocumentSelect = (props) => {
    const searchifyFLV = DocumentFilterableListView

    return GenericPieSelect({
        placeholder: props.multi ? "Choose documents..." : "Choose document...",
        ...props,
        resource: DjangIO.app.document.models.Document,
        advancedSearchifyConfig: generateAdvancedSearchifyConfig(props, searchifyFLV),
        label: DocumentSelect.defaultProps.label,
    })
}
DocumentSelect.defaultProps = { label: "display_text" }

export const DocumentImpactReportSelect = (props) => {
    const searchifyFLV = DocumentFilterableListView

    return GenericPieSelect({
        placeholder: props.multi ? "Choose documents..." : "Choose document...",
        ...props,
        resource: DjangIO.app.document.models.Document,
        advancedSearchifyConfig: generateAdvancedSearchifyConfig(props, searchifyFLV),
        label: DocumentImpactReportSelect.defaultProps.label,
    })
}
DocumentImpactReportSelect.defaultProps = { label: "display_text" }

export const RegulationDocumentSelect = (props) => {
    const searchifyFLV = RegulationDocumentFilterableListView

    return GenericPieSelect({
        placeholder: props.multi ? "Choose regulation documents..." : "Choose regulation document...",
        ...props,
        resource: DjangIO.app.document.models.RegDocument,
        advancedSearchifyConfig: generateAdvancedSearchifyConfig(props, searchifyFLV),
        label: RegulationDocumentSelect.defaultProps.label,
    })
}
RegulationDocumentSelect.defaultProps = { label: "title" }

export const FacebookPostSelect = (props) => (
    <DocumentSelect
        placeholder={props.multi ? "Choose posts..." : "Choose post..."}
        permanentFilter={{ document_type: DjangIO.app.document.models.DocumentType.facebook_post.value }}
        {...props}
    />
)

export const TweetSelect = (props) => (
    <DocumentSelect
        placeholder={props.multi ? "Choose tweets..." : "Choose tweet..."}
        permanentFilter={{ document_type: DjangIO.app.document.models.DocumentType.tweet.value }}
        {...props}
    />
)

export const DCLetterSelect = (props) => (
    <DocumentSelect
        placeholder={props.multi ? "Choose Dear Colleague letter..." : "Choose Dear Colleague letters..."}
        permanentFilter={{ document_type: DjangIO.app.document.models.DocumentType.dc_letter.value }}
        {...props}
    />
)

export const PressReleaseSelect = (props) => (
    <DocumentSelect
        placeholder={props.multi ? "Choose press release..." : "Choose press release..."}
        permanentFilter={{ document_type: DjangIO.app.document.models.DocumentType.press_release.value }}
        {...props}
    />
)

export const FloorStatementSelect = (props) => (
    <DocumentSelect
        placeholder={props.multi ? "Choose floor statement..." : "Choose floor statement..."}
        permanentFilter={{ document_type: DjangIO.app.document.models.DocumentType.floor_statement.value }}
        {...props}
    />
)

export const ColumnSelect = (props) => {
    return (
        <div data-cy="cy-column-select">
            {GenericPieSelect({
                ...props,
                resource: DjangIO.app.userdata.spreadsheet.models.Column,
                placeholder: props.multi ? "Choose columns..." : "Choose column...",
                label: ColumnSelect.defaultProps.label,
            })}
        </div>
    )
}
ColumnSelect.defaultProps = { label: "name" }

export const OrgDesignSelect = (props) => {
    // Allows value to be passed down as string uri (instead of int id) for the purpose of orgDesign relationships without breaking the select
    const value = typeof props.value === "string" ? idFromResourceUri(props.value) : props.value

    return GenericPieSelect({
        placeholder: props.multi ? "Choose designs..." : "Choose design...",
        ...props,
        value,
        resource: DjangIO.app.userdata.models.OrganizationDesign,
        multi: props.multi,
        label: OrgDesignSelect.defaultProps.label,
        returnAllResponse: true,
    })
}
OrgDesignSelect.defaultProps = { label: "design_name" }

export const CustomTagSelect = (props) => {
    return GenericPieSelect({
        placeholder: "Select custom field...",
        ...props,
        resource: DjangIO.app.grassroots.models.CustomTag,
        label: CustomTagSelect.defaultProps.label,
    })
}
CustomTagSelect.defaultProps = { label: "name" }

export const CustomDataSelect = (props) => {
    const searchifyFLV = CustomDataSearchifyFLV

    return GenericPieSelect({
        placeholder: "Select footprint data...",
        ...props,
        resource: DjangIO.app.custom_data.manage_data.models.CustomData,
        advancedSearchifyConfig: generateAdvancedSearchifyConfig(props, searchifyFLV),
        label: CustomDataSelect.defaultProps.label,
    })
}
CustomDataSelect.defaultProps = { label: "category" }

export const CustomRegionSelectionSelect = (props) => {
    return GenericPieSelect({
        placeholder: "Choose custom regions",
        ...props,
        resource: DjangIO.app.userdata.models.CustomRegionSelection,
        label: CustomRegionSelectionSelect.defaultProps.label,
    })
}
CustomRegionSelectionSelect.defaultProps = { label: "name" }

export const EventParticipantSelect = (props) => {
    return GenericPieSelect({
        placeholder: "Select participant...",
        ...props,
        resource: DjangIO.app.events.models.EventParticipant,
        label: EventParticipantSelect.defaultProps.label,
    })
}
EventParticipantSelect.defaultProps = { label: "name" }

export const AttachmentSelect = (props) => {
    return GenericPieSelect({
        placeholder: "Choose a file...",
        ...props,
        resource: DjangIO.app.userdata.models.NonOutboxAttachment,
        label: AttachmentSelect.defaultProps.label,
    })
}
AttachmentSelect.defaultProps = { label: "name" }

export const RegulationSelect = (props) => {
    const searchifyFLV = RegulationFilterableListView

    return GenericPieSelect({
        placeholder: props.multi ? "Choose regulations..." : "Choose a regulation...",
        ...props,
        resource: DjangIO.app.reg.models.Regulation,
        advancedSearchifyConfig: generateAdvancedSearchifyConfig(props, searchifyFLV),
        label: RegulationSelect.defaultProps.label,
    })
}
RegulationSelect.defaultProps = { label: "title" }

export const AgencySelect = (props) => {
    const searchifyFLV = AgencySearchifyFLV
    const permanentFilter = {
        unique_agencies: true,
        ...props.permanentFilter,
    }

    let placeholder = props.multi ? "Choose agencies..." : "Choose an agency..."

    if (Userdata.has_international_region) {
        placeholder = props.multi ? "Choose departments..." : "Choose a department..."
    }

    return GenericPieSelect({
        placeholder,
        ...props,
        resource: DjangIO.app.reg.models.Agency,
        permanentFilter,
        advancedSearchifyConfig: generateAdvancedSearchifyConfig(props, searchifyFLV, permanentFilter),
        label: AgencySelect.defaultProps.label,
    })
}
AgencySelect.defaultProps = { label: "enhanced_agency_name" }

export const DocketSelect = (props) => {
    const searchifyFLV = DocketFilterableListView

    return GenericPieSelect({
        placeholder: props.multi ? "Choose dockets..." : "Choose a docket...",
        ...props,
        resource: DjangIO.app.reg.models.Docket,
        advancedSearchifyConfig: generateAdvancedSearchifyConfig(props, searchifyFLV),
        label: DocketSelect.defaultProps.label,
    })
}
DocketSelect.defaultProps = { label: "title" }

export const AlertSelect = (props) => {
    const searchifyFLV = AlertFilterableListView

    return GenericPieSelect({
        placeholder: props.multi ? "Choose alerts..." : "Choose alert item...",
        ...props,
        resource: DjangIO.app.userdata.alerts.models.Alert,
        advancedSearchifyConfig: generateAdvancedSearchifyConfig(props, searchifyFLV),
        label: AlertSelect.defaultProps.label,
    })
}
AlertSelect.defaultProps = { label: "title" }

export const AlertGroupSelect = (props) => {
    const searchifyFLV = AlertGroupFilterableListView

    return GenericPieSelect({
        placeholder: props.multi ? "Choose alert groups..." : "Choose alert group...",
        ...props,
        resource: DjangIO.app.userdata.alerts.models.AlertGroup,
        advancedSearchifyConfig: generateAdvancedSearchifyConfig(props, searchifyFLV),
        label: AlertGroupSelect.defaultProps.label,
    })
}
AlertGroupSelect.defaultProps = { label: "title" }

export const FeedItemSelect = (props) => {
    const searchifyFLV = AlertFeedItemFilterableListView

    return GenericPieSelect({
        placeholder: props.multi ? "Choose alert feed items..." : "Choose alert feed item...",
        ...props,
        resource: DjangIO.app.userdata.alerts.models.FeedItem,
        advancedSearchifyConfig: generateAdvancedSearchifyConfig(props, searchifyFLV),
        label: FeedItemSelect.defaultProps.label,
    })
}
FeedItemSelect.defaultProps = { label: "name" }

export const AssignmentSelect = (props) => {
    const searchifyFLV = AssignmentFilterableListView

    return GenericPieSelect({
        placeholder: props.multi ? "Choose assignments..." : "Choose assignment...",
        ...props,
        resource: DjangIO.app.userdata.models.Assignment,
        advancedSearchifyConfig: generateAdvancedSearchifyConfig(props, searchifyFLV),
        label: AssignmentSelect.defaultProps.label,
    })
}
AssignmentSelect.defaultProps = { label: "title" }

export const BulkUploadFileSelect = (props) => {
    return GenericPieSelect({
        placeholder: "Choose a bulk upload file...",
        ...props,
        resource: DjangIO.app.custom_data.models.BulkUploadFile,
        label: BulkUploadFileSelect.defaultProps.label,
    })
}
BulkUploadFileSelect.defaultProps = { label: "name" }

export const CustomEventSelect = (props) => {
    return GenericPieSelect({
        placeholder: props.multi ? "Choose custom events..." : "Choose custom event...",
        ...props,
        resource: DjangIO.app.custom_event.models.CustomEvent,
        label: CustomEventSelect.defaultProps.label,
    })
}
CustomEventSelect.defaultProps = { label: "name" }

export const NewDownloadTemplateSelect = (props) => {
    return GenericPieSelect({
        placeholder: "Load an existing template...",
        ...props,
        resource: DjangIO.app.pdfdownload.models.NewDownloadTemplate,
        label: NewDownloadTemplateSelect.defaultProps.label,
    })
}
NewDownloadTemplateSelect.defaultProps = { label: "name" }

export const TrackingDashboardSelect = (props) => {
    const searchifyFLV = TrackingDashboardFilterableListView
    const permanentFilter = Object.assign(
        {
            disabled: false,
        },
        props.permanentFilter || {},
    )

    return GenericPieSelect({
        placeholder: props.multi ? "Choose tracking boards..." : "Choose tracking board...",
        ...props,
        resource: DjangIO.app.tracking_dashboards.models.TrackingDashboard,
        permanentFilter,
        label: TrackingDashboardSelect.defaultProps.label,
        advancedSearchifyConfig: generateAdvancedSearchifyConfig(props, searchifyFLV, permanentFilter),
    })
}
TrackingDashboardSelect.defaultProps = { label: "name" }

export const SendEmailSelect = (props) => {
    const searchifyFLV = SendEmailFilterableListView

    const permanentFilter = Object.assign(
        {
            dehydrate_extra: ["bulk_email"],
        },
        props.permanentFilter || {},
    )
    const renderOption = (option) => {
        const bulkEmailText = option && getNestedObjectKey(option, ["_extra", "bulk_email", "bulk_email__text"])
        const bulkEmailSubject = option && getNestedObjectKey(option, ["_extra", "bulk_email", "bulk_email__subject"])

        return bulkEmailText && bulkEmailSubject
            ? `${option.recipient_name} (${bulkEmailSubject} - ${bulkEmailText.substring(0, 100)}...)`
            : option.recipient_name
    }

    return GenericPieSelect({
        placeholder: props.multi ? "Choose Outbox (Recipient) emails..." : "Choose Outbox (Recipient) email...",
        ...props,
        resource: DjangIO.app.userdata.outbox.models.send_email.SendEmail,
        renderOption,
        permanentFilter,
        label: SendEmailSelect.defaultProps.label,
        advancedSearchifyConfig: generateAdvancedSearchifyConfig(props, searchifyFLV),
    })
}
SendEmailSelect.defaultProps = { label: "recipient_name" }

export const MediaOutletSelect = (props) => {
    const searchifyFLV = PublicOrganizationFilterableListView
    const permanentFilter = {
        // Allows us to call PublicOrganization.objects.for_media(lexis=True)
        for_media_monitoring: true,
    }
    const searchifyPermanentFilter = { order_by: "ln_rank", ...permanentFilter }

    return GenericPieSelect({
        ...props,
        advancedSearchifyConfig: generateAdvancedSearchifyConfig(props, searchifyFLV, searchifyPermanentFilter),
        label: MediaOutletSelect.defaultProps.label,
        permanentFilter,
        placeholder: props.multi ? "Choose outlets..." : "Choose outlet...",
        resource: DjangIO.app.person.models.PublicOrganization,
    })
}
MediaOutletSelect.defaultProps = { label: "name" }

export const ArticleSelect = (props) => {
    const searchifyFLV = ArticleFilterableListView
    const initialFilter = {
        estimated_published_date_range: DjangIO.app.events.models.DateRangeType.today.value,
        language: DjangIO.app.document.types.Language.english.value,
    }
    const searchifyInitialFilters = {
        addedIds: [],
        deletedIds: [],
        querysetList: [initialFilter],
    }
    const apiDateFormat = "YYYY-MM-DD HH:mm:ss"
    const minDate = new Date()
    minDate.setFullYear(minDate.getFullYear() - 2)
    const permanentFilter = {
        ...(props.permanentFilter || {}),
        language: DjangIO.app.document.types.Language.english.value,
        estimated_published_date__gt: moment(minDate).format(apiDateFormat),
    }

    return GenericPieSelect({
        placeholder: props.multi ? "Choose news articles..." : "Choose news article...",
        ...props,
        permanentFilter,
        resource: DjangIO.app.media_monitoring.models.Article,
        advancedSearchifyConfig: generateAdvancedSearchifyConfig(props, searchifyFLV, {}, searchifyInitialFilters),
    })
}
ArticleSelect.defaultProps = { label: "title" }

export const ArticleTopicSelect = (props) => {
    const searchifyFLV = ArticleTopicSearchifyFLV

    return GenericPieSelect({
        placeholder: props.multi ? "Choose news topics/subtopics..." : "Choose news topic/subtopics...",
        resource: DjangIO.app.media_monitoring.models.ArticleTopic,
        advancedSearchifyConfig: generateAdvancedSearchifyConfig(props, searchifyFLV),
        ...props,
        permanentFilter: { order_by: "topic_type" },
    })
}
ArticleTopicSelect.defaultProps = { label: "topic" }

export const BillTermSelect = (props) => {
    return GenericPieSelect({
        permanentFilter: {
            term_type: DjangIO.app.models.TermType.new.value,
            top_term: true,
        },
        placeholder: props.multi ? "Choose policy areas..." : "Choose policy area...",
        ...props,
        resource: DjangIO.app.bill.models.BillTerm,
        label: "name",
    })
}

export const AccountSelect = (props) => {
    return GenericPieSelect({
        placeholder: "Choose an account...",
        ...props,
        resource: DjangIO.app.ledger.models.Account,
        label: "display_label",
        permanentFilter: { excluded_account_types: true },
    })
}

export const LedgerSettingsSelect = (props) => {
    return GenericPieSelect({
        placeholder: "Choose a PAC...",
        ...props,
        resource: DjangIO.app.ledger.models.LedgerSettings,
        label: "name",
    })
}

export const PoliticalCommitteeSelect = (props) => {
    const searchifyFLV = PoliticalCommitteeFilterableListView
    const permanentFilter = {
        ...(props.permanentFilter || {}),
        ...(props.limitToActiveCommittees && { is_active: true }),
        committee_type__isnull: false,
    }

    const label =
        props.customProps && props.customProps.label
            ? props.customProps.label
            : PoliticalCommitteeSelect.defaultProps.label

    return GenericPieSelect({
        placeholder: "Choose a political committee...",
        ...props,
        resource: DjangIO.app.pac.models.political_committee.PoliticalCommittee,
        label,
        permanentFilter,
        advancedSearchifyConfig: generateAdvancedSearchifyConfig(props, searchifyFLV),
    })
}
PoliticalCommitteeSelect.defaultProps = { label: "select_label", limitToActiveCommittees: true }

// We need a version of the PoliticalCommitteeSelect that allows for inactive committees
export const PoliticalCommitteeActiveOrInactiveSelect = (props) => {
    const searchifyFLV = PoliticalCommitteeFilterableListView
    const permanentFilter = {
        ...(props.permanentFilter || {}),
        committee_type__isnull: false,
    }

    const label =
        props.customProps && props.customProps.label
            ? props.customProps.label
            : PoliticalCommitteeSelect.defaultProps.label

    return GenericPieSelect({
        placeholder: "Choose a political committee...",
        ...props,
        resource: DjangIO.app.pac.models.political_committee.PoliticalCommittee,
        label,
        permanentFilter,
        advancedSearchifyConfig: generateAdvancedSearchifyConfig(props, searchifyFLV),
    })
}

export const PoliticalCommitteeCampaignFinanceSelect = (props) => {
    const searchifyFLV = PoliticalCommitteeCampaignFinanceFilterableListView
    const permanentFilter = {
        ...(props.permanentFilter || {}),
        committee_type__isnull: false,
        is_active: true,
    }

    const label =
        props.customProps && props.customProps.label
            ? props.customProps.label
            : PoliticalCommitteeCampaignFinanceSelect.defaultProps.label

    return GenericPieSelect({
        placeholder: "Choose a political committee...",
        ...props,
        resource: DjangIO.app.pac.models.political_committee.PoliticalCommittee,
        label,
        permanentFilter,
        advancedSearchifyConfig: generateAdvancedSearchifyConfig(props, searchifyFLV),
    })
}
PoliticalCommitteeCampaignFinanceSelect.defaultProps = { label: "select_label" }

export const TransactionSelect = (props) => {
    const searchifyFLV = TransactionFilterableListView
    return GenericPieSelect({
        placeholder: "Choose a transaction...",
        ...props,
        resource: DjangIO.app.ledger.models.Transaction,
        label: "entity_associated_with_transaction",
        advancedSearchifyConfig: generateAdvancedSearchifyConfig(props, searchifyFLV),
    })
}

export const ElectionSelect = (props) => {
    const searchifyFLV = ElectionFilterableListView
    const permanentFilter = getPermanentFilter(props.permanentFilter, {})
    return GenericPieSelect({
        placeholder: "Choose an election...",
        ...props,
        renderOption: (item) => {
            if (
                [
                    DjangIO.app.events.types.ElectionType.full_general.value,
                    DjangIO.app.events.types.ElectionType.off_year_general.value,
                ].includes(item.election_type)
            ) {
                return "US " + item.name
            }

            return item.name
        },
        extraFields: ["election_type"],
        resource: DjangIO.app.pac.models.election.Election,
        label: "name",
        advancedSearchifyConfig: generateAdvancedSearchifyConfig(props, searchifyFLV),
        permanentFilter,
        shouldBeUniqueId: true,
        onChange: (value) => {
            if (props.onChange) props.onChange(value)
        },
    })
}

export const ElectionCycleSelect = (props) => {
    return GenericPieSelect({
        placeholder: "Choose an election cycle...",
        ...props,
        resource: DjangIO.app.pac.models.election_cycle.ElectionCycle,
        label: "name",
    })
}

export const BeatSelect = (props) => {
    const searchifyFLV = BeatSearchifyFLV
    const permanentFilter = getPermanentFilter(props.permanentFilter, {})

    return GenericPieSelect({
        placeholder: props.multi ? "Choose beats..." : "Choose beat...",
        ...props,
        resource: DjangIO.app.media_monitoring.models.Beat,
        advancedSearchifyConfig: generateAdvancedSearchifyConfig(props, searchifyFLV, permanentFilter),
        label: BeatSelect.defaultProps.label,
    })
}
BeatSelect.defaultProps = { label: "name" }

export const DMASelect = (props) => {
    return GenericPieSelect({
        placeholder: props.multi ? "Choose DMAs..." : "Choose DMA...",
        ...props,
        resource: DjangIO.app.media_monitoring.models.DesignatedMarketArea,
        label: DMASelect.defaultProps.label,
    })
}
DMASelect.defaultProps = { label: "name" }

export const SchoolDistrictSelect = (props) => {
    const searchifyFLV = PublicOrganizationFilterableListView
    const permanentFilter = getPermanentFilter(props.permanentFilter, {
        all_school_districts: true,
    })

    return GenericPieSelect({
        placeholder: props.multi ? "Choose school districts..." : "Choose school district...",
        ...props,
        resource: DjangIO.app.person.models.PublicOrganization,
        permanentFilter,
        advancedSearchifyConfig: generateAdvancedSearchifyConfig(props, searchifyFLV, permanentFilter),
        label: SchoolDistrictSelect.defaultProps.label,
    })
}
SchoolDistrictSelect.defaultProps = { label: "name" }

export const PacSelect = (props) => {
    const permanentFilter = getPermanentFilter(props.permanentFilter, {
        for_user: true,
    })

    return GenericPieSelect({
        placeholder: "Select a PAC",
        ...props,
        resource: DjangIO.app.pac_classic.models.pac.PacClassicPac,
        permanentFilter,
        label: "name",
    })
}

export const AccountBankSelect = (props) => {
    const permanentFilter = getPermanentFilter(props.permanentFilter, {
        account_type__in: [
            DjangIO.app.ledger.types.LedgerAccountType.checking_account.value,
            DjangIO.app.ledger.types.LedgerAccountType.savings_account.value,
        ],
        bank_name__isnull: false,
    })

    return GenericPieSelect({
        placeholder: props.multi ? "Choose Bank Accounts..." : "Choose a Bank Account...",
        ...props,
        resource: DjangIO.app.ledger.models.Account,
        permanentFilter,
        label: AccountSelect.defaultProps.label,
    })
}
AccountSelect.defaultProps = { label: "bank_name" }

export const TransactionBatchSelect = (props) => {
    return GenericPieSelect({
        placeholder: props.multi ? "Choose Batches..." : "Choose a Batch...",
        ...props,
        resource: DjangIO.app.ledger.models.TransactionBatch,
        label: TransactionBatchSelect.defaultProps.label,
    })
}
TransactionBatchSelect.defaultProps = { label: "display_name" }

export const SocialMediaSelect = (props) => {
    const searchifyFLV = SocialDocumentFilterableListView
    return GenericPieSelect({
        placeholder: props.multi ? "Choose social media documents..." : "Choose social media document...",
        ...props,
        resource: DjangIO.app.document.models.NewDocument,
        label: SocialMediaSelect.defaultProps.label,
        advancedSearchifyConfig: generateAdvancedSearchifyConfig(props, searchifyFLV),
    })
}

SocialMediaSelect.defaultProps = {
    label: "display_text",
    permanentFilter: {
        document_type__in: DjangIO.app.document.models.DocumentType.filter_groups.find(
            (group) => group.title === "Social",
        ).values,
    },
}

// https://developer.candid.org/reference/essentials_v3
export const CharitySelect = (props) => {
    return GenericPieSelect({
        placeholder: props.multi ? "Choose Charities..." : "Choose a Charity...",
        ...props,
        // if this request returns less than the Candid limit,
        // then we know that there is no more data to load
        apiMetaLimit: API_META_LIMIT_CANDID,
        // one second instead of the default 500ms
        customDebounce: 1000,
        // Can be any string you'd like to search on, including EIN, organization name, keywords, etc.
        customFilterLabel: "searchTerms",
        // The maximum number of hits to be returned defaults to 25
        // and the offset from the first result defaults to 0.
        // This, we increment by 25 each time as the user scrolls down the async Select
        customGetter: getCharities,
        customId: (item) => {
            return item.organization.organization_id
        },
        // show the Organization's "label (acronym)"
        customLabel: (item) => {
            const label = [`${item.organization.organization_name}`]

            if (item.organization.also_known_as) {
                label.push(`(${item.organization.also_known_as})`)
            }

            return label.join(" ")
        },
        // The offset from the first result. Defaults to 0.\
        customOffsetLabel: "from",
        // we want to actually store the EIN as the fk/value in the database row
        customValue: (item) => {
            return item.organization.ein
        },
        // app/pac/charities/api.py CharityResource()
        resource: "CharityResource",
        label: CharitySelect.defaultProps.label,
        quickSearchMinimumChars: 3,
    })
}
CharitySelect.defaultProps = { label: "name" }

// Pac Classic
export const PacClassicPacTransactionSelect = (props) => {
    const searchifyFLV = PacClassicPacTransactionFilterableListView
    return GenericPieSelect({
        placeholder: props.multi ? "Choose Transactions (PAC)..." : "Choose a Transactions (PAC)...",
        ...props,
        resource: DjangIO.app.pac_classic.models.transaction.PacClassicPacTransaction,
        label: PacClassicPacTransactionSelect.defaultProps.label,
        advancedSearchifyConfig: generateAdvancedSearchifyConfig(props, searchifyFLV),
    })
}
PacClassicPacTransactionSelect.defaultProps = { label: "entity_name" }

export const PacClassicBankAccountSelect = (props) => {
    return GenericPieSelect({
        placeholder: props.multi ? "Choose Bank Accounts (PAC)..." : "Choose a Bank Account (PAC)...",
        ...props,
        resource: DjangIO.app.pac_classic.models.bank_account.PacClassicBankAccount,
        label: PacClassicBankAccountSelect.defaultProps.label,
    })
}
PacClassicBankAccountSelect.defaultProps = { label: "name" }

export const PacClassicElectionSelect = (props) => {
    return GenericPieSelect({
        placeholder: props.multi ? "Choose Elections (PAC)..." : "Choose an Election (PAC)...",
        ...props,
        resource: DjangIO.app.pac_classic.models.election.PacClassicElection,
        label: PacClassicElectionSelect.defaultProps.label,
    })
}
PacClassicElectionSelect.defaultProps = { label: "name" }

export const PacClassicPacOfficeSelect = (props) => {
    const searchifyFLV = PacClassicPacOfficeFilterableListView
    return GenericPieSelect({
        placeholder: props.multi ? "Choose Elected Offices (PAC)..." : "Choose an Elected Office (PAC)...",
        ...props,
        resource: DjangIO.app.pac_classic.models.office.PacClassicPacOffice,
        label: PacClassicPacOfficeSelect.defaultProps.label,
        advancedSearchifyConfig: generateAdvancedSearchifyConfig(props, searchifyFLV),
    })
}
PacClassicPacOfficeSelect.defaultProps = { label: "name_with_juris" }

export const PacClassicPacOrganizationSelect = (props) => {
    const searchifyFLV = PacClassicPacOrganizationFilterableListView
    return GenericPieSelect({
        placeholder: props.multi ? "Choose PAC Organizations (PAC)..." : "Choose a PAC Organization (PAC)...",
        ...props,
        resource: DjangIO.app.pac_classic.models.pac_organization.PacClassicPacOrganization,
        label: PacClassicPacOrganizationSelect.defaultProps.label,
        advancedSearchifyConfig: generateAdvancedSearchifyConfig(props, searchifyFLV),
    })
}
PacClassicPacOrganizationSelect.defaultProps = { label: "organization_name" }

export const PacClassicOrganizationSelect = (props) => {
    return GenericPieSelect({
        placeholder: props.multi ? "Choose Organization (PAC)..." : "Choose a Organization (PAC)...",
        ...props,
        resource: DjangIO.app.pac_classic.models.organization.PacClassicOrganization,
        label: PacClassicOrganizationSelect.defaultProps.label,
    })
}
PacClassicOrganizationSelect.defaultProps = { label: "full_name" }
